import { createAsyncThunk } from "@reduxjs/toolkit";

import * as basketAPI from "../../../api/basket";
import { IReduxState } from "../reducers.interfaces";
import { getShippingAddress } from "../utils";

/**
 * Arguments for savePreferredDeliveryDate thunk.
 */
interface SavePreferredDeliveryDateArgs {
    date: Date | null;
}

/**
 * Return type for savePreferredDeliveryDate thunk.
 */
interface SavePreferredDeliveryDateResult {
    date: Date | null;
}

/**
 * Save the customer's preferred delivery date for white-glove delivery.
 *
 * This thunk saves the customer's preferred delivery date selection
 * to the basket. The date is stored as an ISO string in Redux state
 * and communicated to the fulfillment system. Only applicable for
 * white-glove delivery shipments.
 *
 * @param {Date | null} args.date - The preferred delivery date (or null to clear)
 * @returns {Promise<{date: Date | null}>} Promise resolving to the saved date
 *
 * @example
 * // Set preferred delivery date
 * dispatch(savePreferredDeliveryDate({
 *   date: new Date('2024-06-15')
 * }));
 *
 * @example
 * // Clear preferred delivery date
 * dispatch(savePreferredDeliveryDate({ date: null }));
 *
 * @sideEffects
 * - Reads shipping postcode from `state.form.shipping_postcode` (truncated to 5 digits)
 * - Makes API call to save date via basketAPI.setPreferredDeliveryDate
 * - Updates `data.preferred_date` with ISO date string via extraReducers
 */
export const savePreferredDeliveryDate = createAsyncThunk(
    "checkout/savePreferredDeliveryDate",
    async (
        args: SavePreferredDeliveryDateArgs,
        { getState },
    ): Promise<SavePreferredDeliveryDateResult> => {
        const state = getState() as { checkout: IReduxState };
        const shippingAddress = getShippingAddress(state.checkout);
        await basketAPI.setPreferredDeliveryDate(
            // Make sure we're using the short zip code
            shippingAddress.postcode.slice(0, 5),
            args.date,
        );
        return { date: args.date };
    },
);
